import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { CalibrationTool } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/CalibrationTool';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { useSnapper } from 'src/components/viewers/SheetsViewerV2/hooks/useSnapper';
import { ViewerExtension } from 'src/components/viewers/common/utils/autodesk';
import Edit2D = Autodesk.Extensions.Edit2D;

type Props = {
  activeTool: ActiveTool;
  setActiveTool: Dispatch<SetStateAction<ActiveTool>>;
  viewer: AggregatedView['viewer'] | undefined;
  initPointA: THREE.Vector3 | null;
  initPointB: THREE.Vector3 | null;
  onChange: (
    pointA: THREE.Vector3 | null,
    pointB: THREE.Vector3 | null
  ) => void;
};

export function useCalibrationTool({
  activeTool,
  setActiveTool,
  viewer,
  initPointA,
  initPointB,
  onChange,
}: Props) {
  const { snapper } = useSnapper(viewer);
  const [edit2D, setEdit2D] = useState<Edit2D | null>(null);
  const toolRef = useRef<CalibrationTool | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep onChange ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Get Edit2D extension
  useEffect(() => {
    if (!viewer) return;

    const ext = viewer.getExtension(ViewerExtension.Edit2D) as Edit2D;
    if (ext && !ext.defaultContext) {
      ext.registerDefaultTools();
    }
    setEdit2D(ext);
  }, [viewer]);

  const isCalibrating = activeTool === ActiveTool.Calibration;

  // Store initial points in refs so they don't cause effect re-runs
  // These are only used when first creating the tool
  const initPointARef = useRef(initPointA);
  const initPointBRef = useRef(initPointB);

  // Update refs when calibration starts (but not during calibration)
  useEffect(() => {
    if (!isCalibrating) {
      initPointARef.current = initPointA;
      initPointBRef.current = initPointB;
    }
  }, [isCalibrating, initPointA, initPointB]);

  const enable = () => {
    setActiveTool((prev) =>
      prev === ActiveTool.Calibration ? prev : ActiveTool.Calibration
    );
  };

  const disable = () => {
    setActiveTool((prev) =>
      prev === ActiveTool.Calibration ? ActiveTool.Default : prev
    );
  };

  // Effect to manage tool lifecycle - only depends on isCalibrating and core dependencies
  useEffect(() => {
    if (!viewer || !snapper || !edit2D) {
      return;
    }

    if (isCalibrating) {
      // Create a stable onChange wrapper that uses the ref
      const stableOnChange = (
        pointA: THREE.Vector3 | null,
        pointB: THREE.Vector3 | null
      ) => {
        onChangeRef.current(pointA, pointB);
      };

      // Create tool instance with current ref values (captured at start of calibration)
      const tool = new CalibrationTool(
        viewer,
        snapper,
        edit2D,
        stableOnChange,
        initPointARef.current,
        initPointBRef.current
      );
      toolRef.current = tool;

      viewer.toolController.registerTool(tool);
      viewer.toolController.registerTool(snapper);
      viewer.disableSelection(true);
      viewer.disableHighlight(true);
      viewer.setContextMenu(null);
      viewer.toolController.activateTool(tool.getName());

      // Cleanup when isCalibrating becomes false or component unmounts
      return () => {
        try {
          viewer.toolController.deactivateTool(tool.getName());
        } catch {
          // Tool may not be activated
        }
        viewer.toolController.deregisterTool(tool);
        toolRef.current = null;
      };
    }
  }, [isCalibrating, viewer, snapper, edit2D]);

  return {
    enable,
    disable,
    isCalibrating,
  };
}
